import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import {
  Trophy,
  RefreshCw,
  Plus,
  X,
  AlertTriangle,
  Copy,
  Check,
  Anchor,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Can from './Can';

// ----------------------------------------------------------------
// Jackpot hits + the POSTED jackpot total
// ----------------------------------------------------------------
// Three things this screen exists for:
//
//   1. Logging a jackpot hit. Until this existed nothing in the app recorded
//      that a jackpot had been WON, so no liability ever reset and the posted
//      meter only ever grew.
//   2. Showing the posted total — the number marketing publishes daily — with
//      every input beside it, so a manager can check the figure on paper
//      before it goes on an asset.
//   3. Showing the funding position next to the promise, which is the thing
//      the Hold'em log was getting wrong.
//
// The posted total is DERIVED, not stored:
//     posted(D) = anchor + accrual(anchor date -> D)
// Backed by /admin/cbs/jackpots/posted, /jackpots/hits and /jackpots/rules.
// ----------------------------------------------------------------

const GAMES = ['HE', 'BJ', 'TCP', 'UTH'];
const GAME_LABEL = {
  HE: "Texas Hold'em",
  BJ: 'Blackjack',
  TCP: 'Three Card Poker',
  UTH: "Ultimate Texas Hold'em",
};

const fmtCents = (c) =>
  c === null || c === undefined
    ? '—'
    : `${c < 0 ? '-' : ''}$${Math.abs(Math.round(c) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const apiErr = (e, fallback) =>
  toast.error(
    (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
      fallback
  );

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalise whatever was typed in the Time box to strict HH:mm.
 *
 * The timestamp is the one place where whitespace is fatal rather than
 * cosmetic. `new Date('2026-10-21T22:25 ')` is Invalid Date, so a single
 * trailing space used to reject the whole hit — behind a supervisor PIN,
 * with an error naming `hitAt`, a field that does not appear on the form.
 * That happened on a live $2,414.00 Blackjack entry (2026-10-22).
 *
 * Accepts 22:25 · 2225 · 8:12 PM · 8:12pm · 812, with any spacing.
 * Returns '' for blank (allowed — the time is optional) and null when it
 * cannot be read, so the caller can say so in the operator's own words.
 */
export function normalizeTime(raw) {
  const s = String(raw == null ? '' : raw).replace(/\u00a0/g, ' ').trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2})\s*:?\s*(\d{2})\s*([ap])\.?m?\.?$/i) || s.match(/^(\d{1,2})\s*:?\s*(\d{2})$/);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const meridiem = m[3] ? m[3].toLowerCase() : null;
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'p' && hour !== 12) hour += 12;
    if (meridiem === 'a' && hour === 12) hour = 0;
  } else if (hour > 23) {
    return null;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export default function JackpotHitsCard({ businessDate, onRequestPin }) {
  const [postedByGame, setPostedByGame] = useState({});
  const [hits, setHits] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [anchoring, setAnchoring] = useState(null); // game whose opening balance is being entered

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        api.get('/admin/cbs/jackpots/posted', { params: { date: businessDate } }),
        api.get('/admin/cbs/jackpots/hits', { params: { limit: 50 } }),
      ]);
      setPostedByGame(pRes.data.games || {});
      setHits(hRes.data.hits || []);
    } catch (e) {
      apiErr(e, 'Failed to load jackpot totals');
    } finally {
      setLoading(false);
    }
    try {
      const rRes = await api.get('/admin/cbs/jackpots/rules');
      setRules(rRes.data.rules || []);
    } catch {
      setRules([]);
    }
  }, [businessDate]);

  useEffect(() => {
    load();
  }, [load]);

  const he = postedByGame.HE;

  const copyPosted = async () => {
    if (!he || !he.anchored) return;
    try {
      await navigator.clipboard.writeText(fmtCents(he.postedCents));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — select the figure and copy it manually.');
    }
  };

  // Anchoring a game sets the figure that gets published, so the amount and
  // date are collected here and the PIN prompt stays what it is — a credential
  // prompt. Pushing data entry into CagePinModal would turn a security prompt
  // into a form builder, which is how the two drift apart.
  const requestAnchor = (game, dollars, onDate, reasonText) => {
    onRequestPin({
      title: `Set the ${game} opening balance`,
      subtitle:
        `${fmtCents(Math.round(dollars * 100))} true on ${onDate}. The posted total reads ` +
        `exactly this figure ON that date and accrues from the day after, so a wrong date ` +
        `shifts every published figure by the daily amount for each day of error.`,
      submitLabel: 'Set opening balance',
      needsReason: true,
      reasonLabel: 'Where this figure came from (recorded)',
      reasonPlaceholder: reasonText || 'e.g. 2026-09 Hold\'em log starting balance',
      onSubmit: async ({ employeeNumber, staffPin, reason }) => {
        await api.post('/admin/cbs/jackpots/rules/opening-balance', {
          employeeNumber,
          staffPin,
          reason,
          game,
          openingBalanceCents: Math.round(dollars * 100),
          openingBalanceDate: onDate,
        });
        toast.success(`${game} anchored`);
        setAnchoring(null);
        await load();
      },
    });
  };

  const requestVoid = (hit) => {
    onRequestPin({
      title: `Void the ${hit.game} hit`,
      subtitle:
        `Appends a reversing entry for ${fmtCents(hit.amountCents)} to ${hit.playerName}. ` +
        `Nothing is deleted — both rows stay on the log.` +
        (hit.resetsMeter
          ? ` This hit re-seeded the meter, so voiding it puts the posted total back to what it would have been.`
          : ''),
      submitLabel: 'Void hit',
      needsReason: true,
      reasonLabel: 'Reason (recorded on the log)',
      onSubmit: async ({ employeeNumber, staffPin, reason }) => {
        await api.post(`/admin/cbs/jackpots/hits/${hit.id}/void`, {
          employeeNumber,
          staffPin,
          reason,
        });
        toast.success('Hit voided');
        await load();
      },
    });
  };

  const voidedIds = useMemo(
    () => new Set(hits.map((h) => h.voidsHitId).filter(Boolean)),
    [hits]
  );

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800 p-5 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-white">
          <Trophy size={18} className="text-amber-400" /> Posted jackpots &amp; hits
        </div>
        <div className="flex items-center gap-2">
          <Can permission="cbs:close_shift">
            <button
              onClick={() => setShowForm((v) => !v)}
              className="flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-amber-400"
            >
              {showForm ? <X size={14} /> : <Plus size={14} />}
              {showForm ? 'Cancel' : 'Log a hit'}
            </button>
          </Can>
          <button
            onClick={load}
            disabled={loading}
            className="rounded-lg border border-gray-600 p-1.5 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* The Hold'em figure gets its own banner: it is the one marketing
          republishes every day, so it should be readable and copyable without
          hunting through a table. */}
      <HoldemBanner posted={he} onCopy={copyPosted} copied={copied} onAnchor={() => setAnchoring('HE')} />

      {anchoring && (
        <AnchorForm
          game={anchoring}
          businessDate={businessDate}
          onCancel={() => setAnchoring(null)}
          onContinue={(dollars, onDate) => requestAnchor(anchoring, dollars, onDate)}
        />
      )}

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        {GAMES.map((g) => (
          <PostedTile
            key={g}
            game={g}
            posted={postedByGame[g]}
            onAnchor={() => setAnchoring(g)}
          />
        ))}
      </div>

      {showForm && (
        <HitForm
          businessDate={businessDate}
          rules={rules}
          onRequestPin={onRequestPin}
          onDone={async () => {
            setShowForm(false);
            await load();
          }}
        />
      )}

      <HitLog hits={hits} voidedIds={voidedIds} onVoid={requestVoid} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function HoldemBanner({ posted, onCopy, copied, onAnchor }) {
  if (!posted) return null;
  if (!posted.anchored) {
    return (
      <div className="rounded-lg border border-amber-600/50 bg-amber-500/10 p-4">
        <div className="flex items-start gap-2 text-sm text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">No Hold'em figure to publish yet</div>
            <div className="mt-1 text-amber-200/80">{posted.reason}</div>
            <Can permission="cbs:jackpot">
              <button
                onClick={onAnchor}
                className="mt-2 flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-amber-400"
              >
                <Anchor size={14} /> Set the opening balance
              </button>
            </Can>
          </div>
        </div>
      </div>
    );
  }
  const a = posted.accrual || {};
  return (
    <div className="rounded-lg border border-amber-600/50 bg-gradient-to-r from-amber-500/15 to-transparent p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-amber-300/80">
            {(posted.config && posted.config.postedLabel) || "Texas Hold'em Jackpot"} · posted{' '}
            {posted.asOf}
          </div>
          <div className="mt-0.5 text-4xl font-bold tabular-nums text-amber-300">
            {fmtCents(posted.postedCents)}
          </div>
          {/* The arithmetic in words, so the figure can be checked by hand
              rather than trusted. */}
          <div className="mt-1 text-xs text-gray-400">
            {fmtCents(posted.anchor && posted.anchor.cents)} on{' '}
            {posted.anchor && posted.anchor.date}
            {posted.anchor && posted.anchor.source === 'reseed'
              ? ' (re-seed after a hit)'
              : ' (opening balance)'}
            {a.days ? ` + ${a.days} ${a.basis === 'calendar_day' ? 'days' : 'gaming days'} = ${fmtCents(a.cents)}` : ''}
          </div>
        </div>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 rounded-lg border border-amber-500/60 px-3 py-1.5 text-sm font-medium text-amber-200 hover:bg-amber-500/20"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy figure'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collected here, not in the PIN prompt. The date is required explicitly and
// never defaulted to today: it is the date the balance is true ON, and the
// posted figure moves by the daily amount for every day it is wrong.
function AnchorForm({ game, businessDate, onCancel, onContinue }) {
  const [amount, setAmount] = useState('');
  const [onDate, setOnDate] = useState(businessDate || todayYmd());

  const go = () => {
    const dollars = Number(String(amount).replace(/[$,]/g, ''));
    if (!Number.isFinite(dollars) || dollars < 0) {
      toast.error('Enter the opening balance in dollars, e.g. 25900.00');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(onDate))) {
      toast.error('Enter the date the balance is true on, as YYYY-MM-DD');
      return;
    }
    onContinue(dollars, onDate);
  };

  const inp =
    'w-full rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none';

  return (
    <div className="mt-3 rounded-lg border border-amber-600/40 bg-gray-900/60 p-4">
      <div className="mb-1 text-sm font-medium text-white">
        {game} opening balance
      </div>
      <p className="mb-3 text-xs text-gray-400">
        The figure the posted meter reads on the date you give. Take it from the game's own
        jackpot log — not from today's poster, unless today is that date.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <label className="mb-1 block text-xs text-gray-400">Balance ($)</label>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} className={inp} placeholder="25900.00" />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs text-gray-400">True on</label>
          <input value={onDate} onChange={(e) => setOnDate(e.target.value)} className={inp} placeholder="YYYY-MM-DD" />
        </div>
        <button
          onClick={go}
          className="rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-gray-900 hover:bg-amber-400"
        >
          Continue (supervisor PIN)
        </button>
        <button onClick={onCancel} className="text-sm text-gray-400 hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PostedTile({ game, posted, onAnchor }) {
  if (!posted) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3 text-sm text-gray-500">
        {game} — no data
      </div>
    );
  }
  if (!posted.anchored) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
        <div className="text-sm font-medium text-gray-300">
          {GAME_LABEL[game]} <span className="text-gray-500">({game})</span>
        </div>
        <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-300/90">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{posted.reason}</span>
        </div>
        <Can permission="cbs:jackpot">
          <button
            onClick={onAnchor}
            className="mt-2 flex items-center gap-1 text-xs font-medium text-amber-400 hover:text-amber-300"
          >
            <Anchor size={12} /> Set opening balance
          </button>
        </Can>
      </div>
    );
  }
  const gap = posted.fundingGapCents;
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-gray-300">
          {GAME_LABEL[game]} <span className="text-gray-500">({game})</span>
        </div>
        <div className="text-xl font-semibold tabular-nums text-white">
          {fmtCents(posted.postedCents)}
        </div>
      </div>
      {/* The promise and the funding, side by side. A negative gap under a
          mandatory daily accrual is the designed state, not a fault — the
          house fronts the difference on a low-drop day — so it is shown
          plainly rather than flagged as an error. */}
      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-gray-500">Promised</div>
          <div className="tabular-nums text-gray-300">
            {fmtCents(posted.accrual && posted.accrual.cents)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">GL credited</div>
          <div className="tabular-nums text-gray-300">{fmtCents(posted.meterCreditedCents)}</div>
        </div>
        <div>
          <div className="text-gray-500">Gap</div>
          <div
            className={`tabular-nums ${gap < 0 ? 'text-amber-300' : 'text-emerald-300'}`}
            title={
              gap < 0
                ? 'The house is fronting the difference. Under a mandatory daily accrual this is expected, not an error.'
                : 'Collected more than was promised over this period.'
            }
          >
            {fmtCents(gap)}
          </div>
        </div>
      </div>
      {posted.prizesPaidCents ? (
        <div className="mt-2 text-xs text-gray-500">
          Prizes paid since the anchor: {fmtCents(posted.prizesPaidCents)}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The paper form, field for field.
function HitForm({ businessDate, rules, onRequestPin, onDone }) {
  const [f, setF] = useState({
    game: 'HE',
    businessDate: businessDate || todayYmd(),
    time: '',
    playerName: '',
    tableNumber: '',
    prizeType: '',
    amount: '',
    resetsMeter: false,
    reseedTo: '',
    signatureCaptured: false,
    floorName: '',
    note: '',
  });
  const set = (k) => (e) =>
    setF((p) => ({
      ...p,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const rule = useMemo(
    () =>
      rules
        .filter((r) => r.game === f.game)
        .sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)))[0] || null,
    [rules, f.game]
  );

  // Default the prize type and the re-seed flag from the game's configured top
  // award, but leave both editable: the operator states what happened, the
  // config only suggests it.
  useEffect(() => {
    setF((p) => ({ ...p, prizeType: (rule && rule.topAwardLabel) || p.prizeType }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.game]);

  const submit = () => {
    const dollars = Number(String(f.amount).replace(/[$,]/g, ''));
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Enter the prize amount in dollars, e.g. 15385.00');
      return;
    }
    if (!f.playerName.trim()) {
      toast.error("Enter the player's name as it appears on the form.");
      return;
    }
    if (!f.prizeType.trim()) {
      toast.error('Enter the prize type.');
      return;
    }
    const reseedDollars = f.reseedTo === '' ? null : Number(String(f.reseedTo).replace(/[$,]/g, ''));
    if (f.resetsMeter && reseedDollars !== null && !Number.isFinite(reseedDollars)) {
      toast.error('The re-seed amount is not a number.');
      return;
    }

    // The two fields that build the timestamp are the only free text here
    // where whitespace is fatal rather than cosmetic, so they are cleaned and
    // checked BEFORE the supervisor is asked for a PIN. Being turned away
    // after entering a PIN, by a message naming a field that is not on the
    // form, is how a real $2,414.00 hit got stuck on 2026-10-22.
    const businessDateClean = String(f.businessDate || '').trim();
    if (!YMD_RE.test(businessDateClean)) {
      toast.error('Enter the gaming day as YYYY-MM-DD, for example 2026-10-21.');
      return;
    }
    const timeClean = normalizeTime(f.time);
    if (timeClean === null) {
      toast.error(`Time "${String(f.time).trim()}" was not understood — enter it as 22:25 or 8:12 PM.`);
      return;
    }
    // Show the operator what was actually understood, so the record they are
    // about to sign for is the record they can see.
    if (businessDateClean !== f.businessDate || timeClean !== f.time) {
      setF((p) => ({ ...p, businessDate: businessDateClean, time: timeClean }));
    }

    onRequestPin({
      title: `Log the ${f.game} jackpot hit`,
      subtitle:
        `${f.playerName.trim()} · ${f.prizeType.trim()} · $${dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })} on ${businessDateClean}${timeClean ? ` at ${timeClean}` : ''}. ` +
        (f.resetsMeter
          ? `This re-seeds the posted meter to ${
              reseedDollars !== null
                ? `$${reseedDollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                : fmtCents(rule && rule.reseedCents)
            }, effective ${businessDateClean} — the meter closes that day at the seed and climbs again from the next.`
          : 'This does NOT reset the posted meter — it reduces the reserve only.') +
        ' The log is append-only; a mistake is corrected by voiding, not editing.',
      submitLabel: 'Log hit',
      onSubmit: async ({ employeeNumber, staffPin }) => {
        await api.post('/admin/cbs/jackpots/hits', {
          employeeNumber,
          staffPin,
          game: f.game,
          businessDate: businessDateClean,
          hitAt: timeClean ? `${businessDateClean}T${timeClean}` : null,
          playerName: f.playerName.trim(),
          tableNumber: f.tableNumber.trim() || null,
          prizeType: f.prizeType.trim(),
          amountCents: Math.round(dollars * 100),
          resetsMeter: f.resetsMeter,
          reseedToCents:
            f.resetsMeter && reseedDollars !== null ? Math.round(reseedDollars * 100) : undefined,
          signatureCaptured: f.signatureCaptured,
          floorName: f.floorName.trim() || null,
          note: f.note.trim() || null,
        });
        toast.success('Hit logged');
        await onDone();
      },
    });
  };

  const inp =
    'w-full rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none';
  const lbl = 'mb-1 block text-xs text-gray-400';

  return (
    <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/60 p-4">
      <div className="mb-3 text-sm font-medium text-white">Jackpot hit form</div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div>
          <label className={lbl}>Game</label>
          <select value={f.game} onChange={set('game')} className={inp}>
            {GAMES.map((g) => (
              <option key={g} value={g}>
                {g} — {GAME_LABEL[g]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Date (gaming day)</label>
          <input value={f.businessDate} onChange={set('businessDate')} className={inp} placeholder="YYYY-MM-DD" />
        </div>
        <div>
          <label className={lbl}>Time</label>
          <input value={f.time} onChange={set('time')} className={inp} placeholder="21:45 or 9:45 PM" />
        </div>
        <div>
          <label className={lbl}>Table</label>
          <input value={f.tableNumber} onChange={set('tableNumber')} className={inp} placeholder="#2" />
        </div>
        <div className="col-span-2">
          <label className={lbl}>Player name</label>
          <input value={f.playerName} onChange={set('playerName')} className={inp} placeholder="As written on the form" />
        </div>
        <div>
          <label className={lbl}>Prize type</label>
          <input value={f.prizeType} onChange={set('prizeType')} className={inp} placeholder="High Hand" />
        </div>
        <div>
          <label className={lbl}>Amount ($)</label>
          <input value={f.amount} onChange={set('amount')} className={inp} placeholder="15385.00" />
        </div>
        <div className="col-span-2">
          <label className={lbl}>Floor</label>
          <input value={f.floorName} onChange={set('floorName')} className={inp} placeholder="Who signed off" />
        </div>
        <div className="col-span-2">
          <label className={lbl}>Note</label>
          <input value={f.note} onChange={set('note')} className={inp} placeholder="Optional" />
        </div>
      </div>

      {/* The one checkbox that can move a published figure. It is stated by the
          operator and never inferred from the prize type: a secondary prize
          mis-filed as a top award would knock the posted jackpot down to the
          seed on a poster. */}
      <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-500/5 p-3">
        <input
          type="checkbox"
          checked={f.resetsMeter}
          onChange={set('resetsMeter')}
          className="mt-0.5 h-4 w-4 accent-amber-500"
        />
        <span className="text-sm text-gray-300">
          <span className="font-medium text-amber-200">This is the top award — reset the meter.</span>
          <span className="mt-0.5 block text-xs text-gray-400">
            Only the top award re-seeds. A secondary prize pays out of the reserve and leaves the
            posted jackpot alone. The meter closes at the seed on the day of the hit, then climbs
            again from the next day.
            {rule ? ` ${f.game} seeds to ${fmtCents(rule.reseedCents)}.` : ''}
          </span>
        </span>
      </label>

      {f.resetsMeter && (
        <div className="mt-2 max-w-xs">
          <label className={lbl}>Re-seed to ($) — blank uses the configured seed</label>
          <input value={f.reseedTo} onChange={set('reseedTo')} className={inp} placeholder={
            rule ? String((rule.reseedCents / 100).toFixed(2)) : '10000.00'
          } />
        </div>
      )}

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-300">
        <input
          type="checkbox"
          checked={f.signatureCaptured}
          onChange={set('signatureCaptured')}
          className="h-4 w-4 accent-amber-500"
        />
        The player signed the paper form
      </label>

      <button
        onClick={submit}
        className="mt-4 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-amber-400"
      >
        Log hit (supervisor PIN)
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function HitLog({ hits, voidedIds, onVoid }) {
  if (!hits.length) {
    return (
      <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/40 p-4 text-sm text-gray-500">
        No jackpot hits logged yet.
      </div>
    );
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Game</th>
            <th className="py-2 pr-3">Player</th>
            <th className="py-2 pr-3">Prize</th>
            <th className="py-2 pr-3 text-right">Amount</th>
            <th className="py-2 pr-3">Reset</th>
            <th className="py-2 pr-3">Floor</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {hits.map((h) => {
            const isVoid = !!h.voidsHitId;
            const wasVoided = voidedIds.has(h.id);
            // Both halves stay visible — the paper trail is the point — but a
            // cancelled pair is struck through so a reader is not misled into
            // counting it.
            const dim = isVoid || wasVoided;
            return (
              <tr
                key={h.id}
                className={`border-b border-gray-800 ${dim ? 'text-gray-500 line-through' : 'text-gray-200'}`}
              >
                <td className="py-2 pr-3 tabular-nums">{String(h.businessDate).slice(0, 10)}</td>
                <td className="py-2 pr-3">{h.game}</td>
                <td className="py-2 pr-3">{h.playerName}</td>
                <td className="py-2 pr-3">{h.prizeType}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{fmtCents(h.amountCents)}</td>
                <td className="py-2 pr-3">
                  {h.resetsMeter ? (
                    <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-300">
                      seed {fmtCents(h.reseedToCents)}
                    </span>
                  ) : isVoid ? (
                    <span className="text-xs text-gray-500">void</span>
                  ) : (
                    <span className="text-xs text-gray-500">—</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-xs">{h.floorName || '—'}</td>
                <td className="py-2 text-right">
                  {!dim && (
                    <Can permission="cbs:close_shift">
                      <button
                        onClick={() => onVoid(h)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Void
                      </button>
                    </Can>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
